import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";

import "./UsernameInput.scss";
import { userProfileAtom } from "../../../jotai/atoms/user";
import useAnalytics from "../../../hooks/useAnalytics";
import {
  handleUpdateOnboardingState,
  handleUpdateUsername,
} from "../../../api-clients/userAccountClient";
import { showSuccessToast } from "../../../utils/show-toast";
import { OnboardingSteps, ROUTES } from "../../../utils/constants";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";

export enum UsernameUpdateOrigin {
  ONBOARDING,
  EDIT_PROFILE,
}
interface UsernameInputProps {
  username: string;
  setUsername: React.Dispatch<React.SetStateAction<string>>;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  focusInputOnMount?: boolean;
  origin?: UsernameUpdateOrigin;
}

function checkIsUsernameValid(input: string) {
  const regex = /^\w{2,15}$/;
  return regex.test(input);
}

export default function UsernameInput({
  username,
  setUsername,
  setIsLoading,
  focusInputOnMount,
  origin,
}: UsernameInputProps) {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { goToStep } = useOnboardingSteps();
  const [userProfile, setUserProfile] = useAtom(userProfileAtom);
  const [error, setError] = useState("");
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });
  const isUsernameUnchanged =
    userProfile?.username && userProfile.username === username;
  const stayOnPage = origin === UsernameUpdateOrigin.EDIT_PROFILE;

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (checkIsUsernameValid(e.currentTarget.value)) {
      error && setError("");
    }
    setUsername(e.currentTarget.value);
  };

  const handleProceed = () => {
    const nextPagePath = `${ROUTES.ROOT.SIGN_UP}/${ROUTES.SIGN_UP.WELCOME}`;
    goToStep(OnboardingSteps.VIEW_WELCOME);
    navigate(nextPagePath);
  };

  async function handleOnContinueClicked() {
    if (!checkIsUsernameValid(username)) {
      const errorMessage = t(
        "username_must_be_between_two_to_fifteen_characters",
      );
      setError(errorMessage);
      return;
    }

    setError("");

    if (origin === UsernameUpdateOrigin.ONBOARDING) {
      analytics.sendClaimProfileEvent(username);
      analytics.sendClaimUsernameEvent();
      await handleUpdateOnboardingState({ claimedUsername: true });
      if (isUsernameUnchanged) {
        showSuccessToast(t("username_claimed"), { top: -4 });
        handleProceed();
        return;
      }
    }

    setIsLoading?.(true);
    const profile = await handleUpdateUsername(username);
    setIsLoading?.(false);
    if (!profile) return;

    const successMessage =
      origin === UsernameUpdateOrigin.ONBOARDING
        ? t("username_claimed")
        : t("username_updated");
    setUserProfile(profile);
    showSuccessToast(successMessage, { top: -4 });

    !stayOnPage && handleProceed();
  }

  useEffect(() => {
    if (focusInputOnMount) usernameInputRef.current?.focus();
  }, [focusInputOnMount]);

  return (
    <form
      id="username-form"
      className="d-flex flex-column align-items-center username-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleOnContinueClicked();
      }}
    >
      <label htmlFor="username" className="text-capitalize username-label">
        {t("username")}
      </label>
      <input
        id="username"
        ref={usernameInputRef}
        value={username}
        name="username"
        placeholder={t("enter_username")}
        autoComplete="username"
        className="username-input"
        onChange={handleInputChange}
      />
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
